/**
 * Payment Policy Utilities — Client-side
 * Mirrors the BA formula used in backend paymentPolicyService.js.
 * Used to compute whether a cart item requires immediate or scheduled payment
 * WITHOUT needing an API round-trip (fast, synchronous, client-side preview).
 *
 * BA Formula:  P = X - (C + 3)
 *   X = days until check-in from today
 *   C = (checkInDate - cancellationDeadline) in days
 *   P > 0 AND cancellationDeadline > 72h away  →  scheduled
 *   P <= 0 OR non-refundable                   →  immediate
 */

const HOURS_BEFORE_DEADLINE = 72;

/**
 * Determine payment scenario for a booking.
 * @param {Object} params
 * @param {boolean} params.isRefundable
 * @param {Date|string|null} params.cancellationDeadline
 * @param {Date|string} params.checkInDate
 * @returns {{ scenario: 'immediate'|'scheduled', scheduledPaymentDate: Date|null, paymentDuration: number|null }}
 */
export function determinePaymentScenario({ isRefundable, cancellationDeadline, checkInDate }) {
  if (!isRefundable || !cancellationDeadline || !checkInDate) {
    return { scenario: 'immediate', scheduledPaymentDate: null, paymentDuration: null };
  }

  const checkIn = new Date(checkInDate);
  const cancellationDate = new Date(cancellationDeadline);
  const now = new Date();

  const daysUntilCheckIn = (checkIn - now) / (1000 * 60 * 60 * 24);
  const cancellationPolicyDays = (checkIn - cancellationDate) / (1000 * 60 * 60 * 24);
  const paymentDuration = daysUntilCheckIn - (cancellationPolicyDays + 3);

  if (paymentDuration <= 0) {
    return { scenario: 'immediate', scheduledPaymentDate: null, paymentDuration: Math.round(paymentDuration * 100) / 100 };
  }

  const hoursUntilDeadline = (cancellationDate - now) / (1000 * 60 * 60);
  if (hoursUntilDeadline < HOURS_BEFORE_DEADLINE) {
    return { scenario: 'immediate', scheduledPaymentDate: null, paymentDuration: Math.round(paymentDuration * 100) / 100 };
  }

  const scheduledPaymentDate = new Date(cancellationDate.getTime() - HOURS_BEFORE_DEADLINE * 60 * 60 * 1000);

  return {
    scenario: 'scheduled',
    scheduledPaymentDate,
    paymentDuration: Math.round(paymentDuration * 100) / 100,
  };
}

/**
 * Resolve a Sabre Deadline object to a concrete Date.
 *
 * Sabre provides two deadline formats:
 *   1. Absolute  — Deadline.AbsoluteDeadline (ISO string)
 *   2. Offset    — Deadline.OffsetDropTime="BeforeArrival" + OffsetUnitMultiplier + OffsetTimeUnit
 *                  e.g. { OffsetTimeUnit:"Day", OffsetUnitMultiplier:3, OffsetDropTime:"BeforeArrival" }
 *                  means 3 days before check-in.
 *
 * @param {Object|null} deadlineObj  — Sabre Deadline sub-object
 * @param {string|Date|null} checkInDate — needed for offset resolution
 * @returns {Date|null}
 */
function resolveSabreDeadline(deadlineObj, checkInDate) {
  if (!deadlineObj) return null;

  // Format 1 – absolute date string
  if (deadlineObj.AbsoluteDeadline) return new Date(deadlineObj.AbsoluteDeadline);

  // Format 2 – offset before arrival
  if (deadlineObj.OffsetDropTime === 'BeforeArrival' && checkInDate) {
    const checkIn    = new Date(checkInDate);
    const multiplier = parseInt(deadlineObj.OffsetUnitMultiplier ?? 0, 10);
    const unit       = (deadlineObj.OffsetTimeUnit ?? 'Day').toLowerCase();

    if (unit === 'day') {
      const d = new Date(checkIn);
      d.setDate(d.getDate() - multiplier);
      return d;
    }
    if (unit === 'hour') {
      return new Date(checkIn.getTime() - multiplier * 60 * 60 * 1000);
    }
    if (unit === 'minute') {
      return new Date(checkIn.getTime() - multiplier * 60 * 1000);
    }
  }

  return null;
}

/**
 * Extract a cancellation deadline Date from a cart item.
 *
 * After cartService.addToCart normalization the Sabre CancelPenalty array is stored
 * directly at cartItem.cancellationPolicy (the CancelPenalty[] array).
 * Each element carries a Deadline object — either AbsoluteDeadline or offset-based.
 *
 * Before normalization (e.g. straight from the add-to-cart payload) the full
 * CancelPenalties wrapper lives at cartItem.rateDetails.cancellationPolicy as
 * { CancelPenalty: [...] }.
 *
 * @param {Object} cartItem
 * @returns {Date|null}
 */
function extractCancellationDeadline(cartItem) {
  if (!cartItem) return null;

  // Direct field (set by checkout service or explicit mapping)
  if (cartItem.cancellationDeadline) return new Date(cartItem.cancellationDeadline);

  const checkInDate = cartItem.checkInDate;

  // Primary path after cartService normalization:
  // cartItem.cancellationPolicy = CancelPenalty[]
  const topPolicy = cartItem.cancellationPolicy;
  if (Array.isArray(topPolicy) && topPolicy.length > 0) {
    const first    = topPolicy[0];
    const resolved = resolveSabreDeadline(first?.Deadline, checkInDate);
    if (resolved)              return resolved;
    if (first?.CancelDeadline) return new Date(first.CancelDeadline);
    if (first?.deadline)       return new Date(first.deadline);
    if (first?.cancellationDate) return new Date(first.cancellationDate);
  }

  // Secondary path (pre-normalization / direct rateDetails):
  // cartItem.rateDetails.cancellationPolicy = { CancelPenalty: [...] }
  const ratePolicy = cartItem.rateDetails?.cancellationPolicy;
  if (ratePolicy && typeof ratePolicy === 'object') {
    const penaltyArr = Array.isArray(ratePolicy) ? ratePolicy : ratePolicy.CancelPenalty;
    if (Array.isArray(penaltyArr) && penaltyArr.length > 0) {
      const first    = penaltyArr[0];
      const resolved = resolveSabreDeadline(first?.Deadline, checkInDate);
      if (resolved)              return resolved;
      if (first?.CancelDeadline) return new Date(first.CancelDeadline);
      if (first?.deadline)       return new Date(first.deadline);
      if (first?.cancellationDate) return new Date(first.cancellationDate);
    }
  }

  return null;
}

/**
 * Determine whether a cart item requires immediate or scheduled payment.
 * @param {Object} cartItem - An item from AgentCart.items
 * @returns {{ type: 'immediate'|'scheduled', scheduledDate: Date|null, cancellationDeadline: Date|null,
 *             label: string, paymentDuration: number|null }}
 */
export function getCartItemPaymentType(cartItem) {
  if (!cartItem || cartItem.type === 'experience') {
    return { type: 'immediate', scheduledDate: null, cancellationDeadline: null, label: 'Charged Now', paymentDuration: null };
  }

  // After cartService normalization: cancellationPolicy is the CancelPenalty array.
  // rateDetails.cancellationPolicy is the CancelPenalties wrapper { CancelPenalty: [...] }.
  // If neither path yields an explicit false, default true (presence of a policy = refundable).
  const isRefundable =
    cartItem.cancellationPolicy?.[0]?.Refundable ??
    cartItem.rateDetails?.cancellationPolicy?.CancelPenalty?.[0]?.Refundable ??
    (Array.isArray(cartItem.cancellationPolicy) && cartItem.cancellationPolicy.length > 0);

  const cancellationDeadline = extractCancellationDeadline(cartItem);
  const checkInDate = cartItem.checkInDate;

  const { scenario, scheduledPaymentDate, paymentDuration } = determinePaymentScenario({
    isRefundable,
    cancellationDeadline,
    checkInDate,
  });

  if (scenario === 'scheduled' && scheduledPaymentDate) {
    const dateStr = scheduledPaymentDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return {
      type: 'scheduled',
      scheduledDate: scheduledPaymentDate,
      cancellationDeadline,
      label: `Charged ${dateStr}`,
      paymentDuration,
    };
  }

  return {
    type: 'immediate',
    scheduledDate: null,
    cancellationDeadline,
    label: 'Charged Now',
    paymentDuration,
  };
}

/**
 * Format a date for display in payment info panels.
 * @param {Date|string} date
 * @returns {string}
 */
export function formatPaymentDate(date) {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
